import { type ReactNode } from 'react'
import {
  ClickableTable,
  ClickableTableBody,
  ClickableTableHead,
  ClickableTableHeader,
} from '@/components/Table/ClickableTable'
import type { TableSortOrder } from '@/components/Table'
import { JobsListEmpty } from '@/features/jobs/components/JobsListEmpty'
import styles from './JobsTable.module.css'
import { Checkbox } from '@/components/Checkbox'
import { type ColumnId } from '@/features/jobs/list/jobs-table-columns'

/**
 * Maps columns to their respective styles.
 */
export const jobTableColumnStyles: Partial<Record<ColumnId, string>> = {
  jobTitle: styles.primaryColumn,
  jobShortId: styles.shortIdColumn,
  legendName: styles.jobTypeColumn,
  workspace: styles.workspaceColumn,
  status: styles.statusColumn,
  clientTeam: styles.teamColumn,
  progressionStep: styles.progressionColumn,
  jobScopeChangeRequestStatus: styles.scopeChangeRequestStatusColumn,
  clientChampion: styles.championColumn,
  clientChampionEmail: styles.championColumn,
  proChampion: styles.championColumn,
  proChampionEmail: styles.championColumn,
  isRequestedPro: styles.isRequestedProColumn,
  payout: styles.paymentColumn,
  total: styles.paymentColumn,
  totalPaid: styles.paymentColumn,
  created: styles.dateColumn,
  submitted: styles.dateColumn,
  claimed: styles.dateColumn,
  completed: styles.dateColumn,
  lastProMessageActivity: styles.activityColumn,
  lastClientMessageActivity: styles.activityColumn,
  lastRelationshipManagerMessageActivity: styles.activityColumn,
}

export interface JobsTableColumn {
  id: ColumnId
  header: string
  sortable: boolean
  sortOrder: TableSortOrder
  onSort: (sortOrder: TableSortOrder) => void
}

export interface JobsTableProps {
  isEmpty: boolean
  columns: JobsTableColumn[]
  allSelected: boolean | 'indeterminate'
  onToggleAllSelected: () => void
  isSelectAllPending: boolean
  scrollLoader: ReactNode
  onClearSearchFilter: () => void
  onSetJobScope?: () => void
  corporateMode: boolean
  children: ReactNode
}

export function JobsTable({
  isEmpty,
  columns,
  allSelected,
  onToggleAllSelected,
  isSelectAllPending,
  scrollLoader,
  onClearSearchFilter,
  onSetJobScope,
  corporateMode,
  children,
}: JobsTableProps) {
  return (
    <div>
      {!isEmpty ? (
        <>
          <ClickableTable striped className={styles.jobsTable}>
            <ClickableTableHead className={styles.tableHead}>
              {columns.map((col) =>
                col.id === 'checkbox' ? (
                  <ClickableTableHeader
                    key={col.id}
                    alignment={'left'}
                    className={styles.checkboxColumn}
                  >
                    <Checkbox
                      onChange={onToggleAllSelected}
                      checked={allSelected}
                      isPending={isSelectAllPending}
                    />
                  </ClickableTableHeader>
                ) : (
                  <ClickableTableHeader
                    className={jobTableColumnStyles[col.id]}
                    key={col.id}
                    canSort={col.sortable}
                    sortOrder={col.sortOrder}
                    onSort={col.onSort}
                  >
                    {col.header}
                  </ClickableTableHeader>
                ),
              )}
            </ClickableTableHead>
            <ClickableTableBody>{children}</ClickableTableBody>
          </ClickableTable>
          {scrollLoader}
        </>
      ) : (
        <div className={styles.emptyView}>
          <JobsListEmpty
            onClearSearchFilter={onClearSearchFilter}
            onSetJobScope={onSetJobScope}
            corporateMode={corporateMode}
          />
        </div>
      )}
    </div>
  )
}
